import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Play, Square, LogOut, Wifi, Globe, Pencil, Plus, History, EyeOff, BookOpen, Database } from 'lucide-react';
import { useAlarmCatalogPanel } from './store/alarmCatalogPanel';
import { SantaColomaMapView } from './views/SantaColomaMapView';
import { useColomaMqtt } from './store/coloma-mqtt';
import { useMockMode } from './store/mockMode';
import { useDetailedSimulation } from './store/detailedSimulation';
import { useMqttAlarms } from './hooks/useMqttAlarms';
import { HealthBadge, type HealthStatus } from './components/guadalajara/HealthBadge';
import { santaColomaIntersections } from './data/santa-coloma';
import { useDemoTourRunner } from './hooks/useDemoTour';
import { RequireAuth } from './components/RequireAuth';
import { OverridesGate } from './components/OverridesGate';
import { ErrorBoundary } from './components/ErrorBoundary';
import { useAuth } from './hooks/useAuth';
import { useMapMode } from './store/mapMode';
import { isGoogle3DConfigured } from './components/map/useGoogleMaps3D';
import { useEditMode } from './store/editMode';
import { useAlarmMasks } from './store/alarmMasks';
import { useAlarmAcks, findAck } from './store/alarmAcks';
import { toCanonicalId } from './utils/canonicalId';
import { SearchBox } from './components/SearchBox';
import { formatConsumo } from './utils/formatConsumo';
import { isActiveAlarm } from './components/guadalajara/AlarmPanel';
import { ObsiaChatWidget } from './components/ObsiaChatWidget';

const SHARED_STATE_POLL_MS = 30_000;
const CONSUMO_POLL_MS = 300_000;

function AppContent() {
  const user = useAuth(s => s.user);
  const logout = useAuth(s => s.logout);

  // Conectar al broker MQTT — recepción de estado real de los reguladores.
  // Las máscaras (ARS-09) se cargan antes: los status retenidos llegan nada
  // más suscribirse y, sin máscaras aún, una alarma enmascarada movería la
  // cámara al cargar la página. Si el gateway falla, fetchMasks no lanza.
  useEffect(() => {
    const { connect, disconnect } = useColomaMqtt.getState();
    useAlarmMasks.getState().fetchMasks().finally(connect);
    return () => disconnect();
  }, []);

  // Estado compartido entre operadores: máscaras (ARS-09) y reconocimientos
  // (ARS-17/18). El polling recoge lo que hacen otros en sus sesiones — sin
  // él, un enmascarado puesto por otro admin no se veía hasta recargar.
  useEffect(() => {
    useAlarmAcks.getState().fetchAcks();
    const timer = window.setInterval(() => {
      useAlarmMasks.getState().fetchMasks();
      useAlarmAcks.getState().fetchAcks();
    }, SHARED_STATE_POLL_MS);
    return () => window.clearInterval(timer);
  }, []);

  // Modo "red simulada" (demo): arranca/para la simulación de fases que anima
  // los reguladores sin datos MQTT reales.
  const mockEnabled = useMockMode(s => s.enabled);
  const toggleMock = useMockMode(s => s.toggle);
  useEffect(() => {
    const sim = useDetailedSimulation.getState();
    if (mockEnabled) sim.start();
    else sim.stop();
  }, [mockEnabled]);

  // Reguladores realmente comunicando ahora — el store ya marca offline los
  // que dejan de publicar (staleness 20s, coloma-mqtt.ts), así que este
  // conteo refleja tráfico MQTT en vivo, no el tamaño del catálogo.
  const totalReguladores = santaColomaIntersections.length;
  const enComunicacion = useColomaMqtt(
    s => Object.values(s.reguladores).filter(r => r.online).length,
  );
  const globalHealth: HealthStatus = enComunicacion > 0 ? 'green' : 'red';

  // Reguladores online que no reportan estado por grupo (grupos: [] en su
  // status MQTT) — visto en producción 2026-09-10: el driver real de ARS93
  // manda online/plan/fase bien, pero nunca el color de cada semáforo. El
  // mapa no puede inventarlo (se ve "apagado"), así que se avisa aquí en vez
  // de dejar que parezca un fallo del dashboard. Ver ROADMAP.md.
  const reguladoresSinGrupos = useColomaMqtt(
    s => Object.values(s.reguladores).filter(r => r.online && r.grupos.length === 0).length,
  );
  const alarms = useMqttAlarms();
  const acks = useAlarmAcks(s => s.acks);
  // Al aparecer/reaparecer/resolverse una alarma el estado de reconocimiento
  // puede haber cambiado en el backend — refetch inmediato en vez de esperar
  // al polling. Clave estable por ocurrencia: `alarms` es un array nuevo en
  // cada tick MQTT, la clave solo cambia cuando cambia el conjunto activo.
  const activeOccurrencesKey = useMemo(
    () => alarms.filter(a => !a.resolvedAt).map(a => `${a.driverId}:${a.code}:${a.timestamp}`).join('|'),
    [alarms],
  );
  useEffect(() => {
    useAlarmAcks.getState().fetchAcks();
  }, [activeOccurrencesKey]);
  const { touring, run: runTour, cancel: cancelTour } = useDemoTourRunner();

  // Toggle vista realista (Google Photorealistic 3D Tiles) — solo admin en
  // producción; en dev (VITE_AUTH_DISABLED=true) el guard se relaja para
  // poder iterar sin SSO real.
  const isAdmin = useAuth(s => s.user?.is_admin === true);
  const isAuthDisabled = import.meta.env.VITE_AUTH_DISABLED === 'true';
  const mapMode = useMapMode(s => s.mode);
  const toggleMapMode = useMapMode(s => s.toggle);
  const showMapToggle = (isAdmin || isAuthDisabled) && isGoogle3DConfigured();

  // Modo Edición — solo admin. Corrige la posición GPS de un regulador
  // directamente en el dashboard (ver RegulatorEditPanel).
  const showEditMode = isAdmin || isAuthDisabled;

  // Asistente OBSIA — solo admin por decisión del 2026-10-16: las consultas
  // salen a un modelo externo, así que de momento no es superficie de
  // operador municipal. Sin `VITE_OBSIA_URL` tampoco se monta.
  const showAssistant = isAdmin || isAuthDisabled;

  // Consumo de datos del mes (solo admin). Cada regulador lleva su SIM y el
  // operador factura por MB: el indicador existe para que un equipo mal
  // configurado se vea antes de que llegue la factura, no después.
  const [consumo, setConsumo] = useState<{ mb: number; anomalos: number } | null>(null);
  useEffect(() => {
    if (!showEditMode) return;
    const traer = () => fetch('/admin/consumo/datos?dias=1', { credentials: 'include' })
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (d) setConsumo({ mb: d.mes.mb, anomalos: d.mes.anomalos }); })
      .catch(() => {});
    traer();
    // El meter vuelca cada 60 s y el dato es mensual: no hace falta más.
    const t = window.setInterval(traer, CONSUMO_POLL_MS);
    return () => window.clearInterval(t);
  }, [showEditMode]);

  const editModeActive = useEditMode(s => s.active);
  const toggleEditMode = useEditMode(s => s.toggle);
  const startAddingRegulator = useEditMode(s => s.startAddingRegulator);
  const startAddingSignal = useEditMode(s => s.startAddingSignal);

  return (
    <div className="h-screen bg-gray-950 flex flex-col overflow-hidden">
      {/* Header */}
      <header className="bg-gray-900/80 backdrop-blur-sm border-b border-gray-800 px-3 py-2 flex-shrink-0 z-50">
        <div className="flex items-center justify-between gap-3">
          {/* Bloque de marca: no encoge (el subtítulo se partía en tres
              líneas y el KPI se le montaba encima en anchos medios). */}
          <div className="flex items-center gap-3 flex-shrink-0">
            <img src="/logo-ars93.jpg" alt="ARS_93" className="h-8 rounded" />
            <div>
              <h1 className="text-sm font-semibold text-white leading-tight">ARS_93</h1>
              <p className="text-[10px] text-gray-500 whitespace-nowrap hidden md:block">{user?.org_name || 'Santa Coloma de Gramenet'}</p>
            </div>
          </div>

          <div className="flex items-center gap-2 xl:gap-3 min-w-0">
            {/* KPI resumen — cuántos reguladores del catálogo publican por MQTT ahora mismo */}
            <div data-highlight="header-kpis" className="hidden lg:flex items-center gap-2 px-2 py-1.5 bg-gray-800/60 rounded-lg">
              <HealthBadge status={globalHealth} size={7} />
              <span className="text-[10px] font-mono text-gray-300">{enComunicacion}/{totalReguladores} comunicando</span>
            </div>

            {/* Mobile: global health only */}
            <div className="lg:hidden flex items-center gap-1.5 px-2 py-1.5 bg-gray-800/60 rounded-lg">
              <HealthBadge status={globalHealth} size={7} />
              <span className="text-[11px] font-mono text-gray-300">{enComunicacion}/{totalReguladores}</span>
            </div>

            <SearchBox />

            {/* Vista realista — Google Photorealistic 3D Tiles. Solo admin;
                requiere VITE_GOOGLE_3D_KEY configurada. */}
            {showMapToggle && (
              <button
                onClick={toggleMapMode}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-[11px] font-medium transition-colors ${
                  mapMode === 'realistic'
                    ? 'bg-purple-900/40 border-purple-600/50 text-purple-300 hover:bg-purple-900/60'
                    : 'bg-gray-800/60 border-gray-700/50 text-gray-400 hover:bg-gray-700/60'
                }`}
                title={mapMode === 'realistic'
                  ? 'Volver a la vista operativa (MapLibre 2.5D)'
                  : 'Vista 3D fotorrealista (Google Photorealistic 3D Tiles — demo)'}
              >
                <Globe className="w-3 h-3" />
                <span className="hidden xl:inline">{mapMode === 'realistic' ? 'Vista operativa' : 'Vista realista'}</span>
              </button>
            )}

            {/* Modo Edición — solo admin: corregir posición GPS de un regulador */}
            {showEditMode && (
              <button
                onClick={toggleEditMode}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-[11px] font-medium transition-colors ${
                  editModeActive
                    ? 'bg-amber-900/40 border-amber-600/50 text-amber-300 hover:bg-amber-900/60'
                    : 'bg-gray-800/60 border-gray-700/50 text-gray-400 hover:bg-gray-700/60'
                }`}
                title={editModeActive ? 'Salir del Modo Edición' : 'Modo Edición: corregir posición de reguladores'}
              >
                <Pencil className="w-3 h-3" />
                <span className="hidden xl:inline">Modo Edición</span>
              </button>
            )}

            {/* Histórico de alarmas/eventos/cambios de Modo Edición — solo admin */}
            {showEditMode && (
              <a
                href="/admin/events"
                target="_blank"
                rel="noreferrer"
                title="Histórico de alarmas, eventos y cambios de Modo Edición"
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-[11px] font-medium
                           bg-gray-800/60 border-gray-700/50 text-gray-400 hover:bg-gray-700/60 transition-colors"
              >
                <History className="w-3 h-3" />
                <span className="hidden xl:inline">Historial</span>
              </a>
            )}

            {/* Consumo de datos del mes — solo admin. Dato a secas, sin
                tono de alarma: el detalle está en /admin/consumo. */}
            {showEditMode && consumo && (
              <a
                href="/admin/consumo"
                target="_blank"
                rel="noreferrer"
                title={`${formatConsumo(consumo.mb)} de datos este mes`}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-[11px] font-medium
                           bg-gray-800/60 border-gray-700/50 text-gray-400 hover:bg-gray-700/60 transition-colors"
              >
                <Database className="w-3 h-3" />
                <span className="font-mono">{formatConsumo(consumo.mb)}</span>
              </a>
            )}

            {/* Chuleta de códigos de alarma — para todos, no solo admin */}
            <button
              onClick={() => useAlarmCatalogPanel.getState().toggle()}
              title="Chuleta de códigos de alarma"
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-[11px] font-medium
                         bg-gray-800/60 border-gray-700/50 text-gray-400 hover:bg-gray-700/60 transition-colors"
            >
              <BookOpen className="w-3 h-3" />
              <span className="hidden xl:inline">Alarmas</span>
            </button>

            {/* Añadir regulador/semáforo (ARS-05) — solo con Modo Edición activo */}
            {showEditMode && editModeActive && (
              <>
                <button
                  onClick={startAddingRegulator}
                  title="Añadir un regulador nuevo"
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-[11px] font-medium
                             bg-gray-800/60 border-gray-700/50 text-gray-400 hover:bg-gray-700/60 transition-colors"
                >
                  <Plus className="w-3 h-3" />
                  <span className="hidden xl:inline">Regulador</span>
                </button>
                <button
                  onClick={startAddingSignal}
                  title="Añadir un semáforo nuevo"
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-[11px] font-medium
                             bg-gray-800/60 border-gray-700/50 text-gray-400 hover:bg-gray-700/60 transition-colors"
                >
                  <Plus className="w-3 h-3" />
                  <span className="hidden xl:inline">Semáforo</span>
                </button>
              </>
            )}


            {/* Modo red simulada y Demo tour — herramientas comerciales, solo
                admin: un operador no debe poder pintar datos falsos sobre el
                dashboard real (mismo gate que Modo Edición). */}
            {showEditMode && (<>
            <button
              onClick={toggleMock}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-[11px] font-medium transition-colors ${
                mockEnabled
                  ? 'bg-green-900/40 border-green-600/50 text-green-400 hover:bg-green-900/60'
                  : 'bg-gray-800/60 border-gray-700/50 text-gray-400 hover:bg-gray-700/60'
              }`}
              title={mockEnabled
                ? 'Volver al estado real de los reguladores'
                : 'Simular toda la red conectada (demostración)'}
            >
              <Wifi className="w-3 h-3" />
              <span className="hidden xl:inline">{mockEnabled ? 'Red simulada' : 'Simular red'}</span>
            </button>

            {/* Demo tour button */}
            <button
              onClick={touring ? cancelTour : runTour}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-[11px] font-medium transition-colors ${
                touring
                  ? 'bg-red-900/40 border-red-600/50 text-red-400 hover:bg-red-900/60'
                  : 'bg-cyan-900/40 border-cyan-600/50 text-cyan-400 hover:bg-cyan-900/60'
              }`}
            >
              {touring ? (
                <><Square className="w-3 h-3" /><span className="hidden xl:inline">Parar</span></>
              ) : (
                <><Play className="w-3 h-3" /><span className="hidden xl:inline">Demo</span></>
              )}
            </button>
            </>)}

            {/* Alarmas activas — en rojo solo mientras quede alguna sin
                reconocer; todas reconocidas = ámbar (hay incidencia, pero
                alguien se ha hecho cargo). Las enmascaradas/silenciadas no
                cuentan, igual que en el panel y los badges del mapa. */}
            {(() => {
              const active = alarms.filter(isActiveAlarm);
              const unacked = active.filter(a => !findAck(acks, toCanonicalId(a.driverId), a.code)).length;
              const tone = unacked > 0 ? 'red' : active.length > 0 ? 'amber' : 'gray';
              const cls = {
                red: ['bg-red-900/30', 'text-red-400'],
                amber: ['bg-amber-900/30', 'text-amber-400'],
                gray: ['bg-gray-800/60', 'text-gray-500'],
              }[tone];
              return (
                <div
                  className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg ${cls[0]}`}
                  title={active.length === 0 ? 'Sin alarmas activas'
                    : unacked < active.length
                      ? `${unacked} sin reconocer de ${active.length} activa(s)`
                      : `${active.length} alarma(s) activa(s), ninguna reconocida`}
                >
                  <AlertTriangle className={`w-3 h-3 ${tone === 'gray' ? 'text-gray-600' : cls[1]}`} />
                  <span className={`text-[11px] font-mono font-medium ${cls[1]}`}>
                    {unacked < active.length ? `${unacked} / ${active.length}` : active.length}
                  </span>
                </div>
              );
            })()}

            {/* Reguladores online sin dato de grupo (driver ARS93) — solo admin */}
            {isAdmin && reguladoresSinGrupos > 0 && (
              <div
                className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg bg-amber-900/30 border border-amber-700/40"
                title={`${reguladoresSinGrupos} regulador(es) online sin estado de grupo en su MQTT — el driver de ARS93 no manda "grupos", así que sus semáforos se ven apagados aunque estén activos. No es un fallo del dashboard.`}
              >
                <EyeOff className="w-3 h-3 text-amber-400" />
                <span className="text-[11px] font-mono font-medium text-amber-400">{reguladoresSinGrupos}</span>
              </div>
            )}

            {/* User + logout */}
            {user && (
              <button
                onClick={logout}
                className="flex items-center gap-1.5 px-2 py-1.5 bg-gray-800/60 rounded-lg hover:bg-gray-700/60 transition-colors"
                title={`${user.email} — Cerrar sesión`}
              >
                <span className="text-[10px] font-mono text-gray-400 hidden sm:inline">{user.email}</span>
                <LogOut className="w-3 h-3 text-gray-500" />
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Map fullscreen */}
      <main className="flex-1 overflow-hidden">
        <SantaColomaMapView alarms={alarms} />
        {showAssistant && <ObsiaChatWidget />}
      </main>
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <RequireAuth>
        <OverridesGate>
          <AppContent />
        </OverridesGate>
      </RequireAuth>
    </ErrorBoundary>
  );
}

export default App;
